import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { getAudience } from './config/permissions';
import ProtectedRoute from './components/ProtectedRoute';
import { SIDEBAR_LINKS, UserRole } from './config/permissions';
import { ToastProvider } from './context/ToastContext';
import InstallPrompt from './components/InstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * ✅ ROUTING VERIFICATION (Feb 9, 2026)
 * - BrowserRouter active for clean URLs (no # in URLs)
 * - Azure Static Website configured with error document: index.html
 * - Deep links work correctly: /set-password/<token>, /reset-password/<token>
 * - SPA refresh handling enabled
 */

// Layout
import Sidebar from './components/Sidebar';
import Header from './components/Header';

// Auth
import Login from './pages/Login';
import Logout from './pages/Logout';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import ChangePassword from './pages/ChangePassword';

// Business Registration
import BusinessRegistration from './pages/BusinessRegistration';
import Register from './pages/Register';
import PaymentPage from './pages/PaymentPage';
import RegistrationSuccess from './pages/RegistrationSuccess';
import RegistrationCancel from './pages/RegistrationCancel';
import RegistrationFailed from './pages/RegistrationFailed';
import RegistrationStatus from './pages/RegistrationStatus';

// Public Business Website
import PublicBusinessWebsite from './pages/wholesale/PublicBusinessWebsite';
import PublicShop from './pages/wholesale/PublicShop';
import ShopProductView from './pages/wholesale/ShopProductView';
import ShopLogin from './pages/wholesale/ShopLogin';
import ShopRegister from './pages/wholesale/ShopRegister';
import ShopAcceptInvite from './pages/wholesale/ShopAcceptInvite';
import ShopTrackOrder from './pages/wholesale/ShopTrackOrder';
import ShopForgotPassword from './pages/wholesale/ShopForgotPassword';
import ShopResetPassword from './pages/wholesale/ShopResetPassword';
import CustomerDashboard from './pages/wholesale/CustomerDashboard';
import OrderConfirmed from './pages/wholesale/OrderConfirmed';
import TenantLogin from './pages/tenant/TenantLogin';
import TenantAcceptInvite from './pages/tenant/TenantAcceptInvite';
import TenantPortal from './pages/tenant/TenantPortal';
import TenantForgotPassword from './pages/tenant/TenantForgotPassword';
import TenantResetPassword from './pages/tenant/TenantResetPassword';

// Dashboard
import Dashboard from './pages/wholesale/Dashboard';

// Customers
import AddCustomer from './pages/wholesale/AddCustomer';
import CustomerList from './components/CustomerList';
import EditCustomer from './pages/wholesale/EditCustomer';
import CustomerView from './pages/wholesale/Customerview';
import CustomerOrders from './pages/wholesale/CustomerOrders';

// Products
import ProductList from './pages/wholesale/ProductList';
import ProductForm from './pages/wholesale/ProductForm';
import ProductView from './pages/wholesale/ProductView';
import ProductEdit from './pages/wholesale/ProductEdit';

// Invoices
import InvoiceForm from './components/InvoiceForm';
import InvoiceList from './pages/wholesale/InvoiceList';
import InvoiceView from './pages/wholesale/InvoiceView';
import InvoiceEdit from './pages/wholesale/InvoiceEdit';
import PaymentCheckout from './pages/PaymentCheckout';

// Credit Notes
import CreditNotes from './pages/wholesale/CreditNotes';

// Orders
import OrderList from './pages/wholesale/OrderList';
import OrderForm from './pages/wholesale/OrderForm';
import OrderView from './pages/wholesale/OrderView';

// Inventory Modules
import PurchaseOrders from './pages/wholesale/PurchaseOrders';
import StockMovements from './pages/wholesale/StockMovements';

// Suppliers
import SupplierForm from './pages/wholesale/SupplierForm';
import SupplierList from './pages/wholesale/SupplierList';
import SupplierView from './pages/wholesale/SupplierView';
import SupplierEdit from './pages/wholesale/SupplierEdit';

// Categories
import CategoryForm from './pages/wholesale/CategoryForm';
import CategoryList from './pages/wholesale/CategoryList';
import CategoryEdit from './pages/wholesale/CategoryEdit';
import CategoryView from './pages/wholesale/CategoryView';

// Booking
import BookingCalendar from './pages/wholesale/BookingCalendar';
import BookingForm from './pages/wholesale/BookingForm';
import BookingList from './pages/wholesale/BookingList';
import BookingAnalytics from './pages/wholesale/BookingAnalytics';
import PublicBooking from './pages/wholesale/PublicBooking';

// Business Settings
import BusinessSettings from './pages/wholesale/BusinessSettings';
import EmailSettings from './pages/wholesale/EmailSettings';

// Staff Management
import StaffManagement from './pages/wholesale/StaffManagement';

// Super Admin
import {
  PlatformLayout,
  SuperAdminRoute,
  OverviewPage,
  ApplicationsPage,
  BillingSupportPage,
  BookingsPage,
  BusinessesPage,
  GdprPage,
  SystemPage,
} from './pages/platform';

// Other Modules
import SendMessage from './pages/wholesale/SendMessage';
import CalendarSettings from './pages/wholesale/CalendarSettings';
import StripeConnectSettings from './pages/StripeConnectSettings';
import Booking from './pages/wholesale/Booking';
import BeautyBookings from './pages/beauty/Bookings';
import Accounting from './pages/wholesale/Accounting';
import Analytics from './pages/wholesale/Analytics';
import CreditTerms from './pages/wholesale/CreditTerms';
import AccountsReceivable from './pages/wholesale/AccountsReceivable';
import Shipping from './pages/wholesale/Shipping';
import AIAssistant from './pages/AIAssistant';
import ARAgingReport from './pages/wholesale/ARAgingReport';
import Support from './pages/wholesale/Support';
import AuditLog from './pages/wholesale/AuditLog';
import SessionExpired from './pages/SessionExpired';
import LandlordDashboard from './pages/landlord/LandlordDashboard';
import LandlordProperties from './pages/landlord/Properties';
import PropertyFormPage from './pages/landlord/PropertyFormPage';
import PropertyViewPage from './pages/landlord/PropertyViewPage';
import LandlordTenants from './pages/landlord/Tenants';
import TenantFormPage from './pages/landlord/TenantFormPage';
import TenantViewPage from './pages/landlord/TenantViewPage';
import LandlordLeases from './pages/landlord/Leases';
import LeaseFormPage from './pages/landlord/LeaseFormPage';
import LeaseViewPage from './pages/landlord/LeaseViewPage';
import LandlordRentTracker from './pages/landlord/RentTracker';
import LandlordMaintenance from './pages/landlord/Maintenance';
import LandlordCertificates from './pages/landlord/Certificates';
import LandlordViewings from './pages/landlord/Viewings';
import LandlordInventory from './pages/landlord/Inventory';
import LandlordAuditLog from './pages/landlord/LandlordAuditLog';
import LandlordAccounting from './pages/landlord/Accounting';
import LandlordTaxReport from './pages/landlord/TaxReport';
import LandlordSalesPipeline from './pages/landlord/SalesPipeline';
import PipelineFormPage from './pages/landlord/PipelineFormPage';
import PipelineViewPage from './pages/landlord/PipelineViewPage';

import BeautyDashboard from './pages/beauty/Dashboard';


import './App.css';

const AppRoutes: React.FC = () => {

  const { token, role } = useAuth();

  return (
    <Routes>
      {/* Legal Pages */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/session-expired" element={<SessionExpired />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password/:token" element={<SetPassword />} />
      <Route path="/change-password" element={<ChangePassword />} />

      {/* Business Registration */}
      <Route path="/business-registration" element={<BusinessRegistration />} />
      <Route path="/pricing" element={<BusinessRegistration />} />
      <Route path="/register" element={<BusinessRegistration />} />
      <Route path="/payment" element={<PaymentPage />} />
      <Route path="/registration-success" element={<RegistrationSuccess />} />
      <Route path="/registration-cancel" element={<RegistrationCancel />} />
      <Route path="/registration/success" element={<RegistrationSuccess />} />
      <Route path="/registration/failed" element={<RegistrationFailed />} />
      <Route path="/registration/status/:registrationId" element={<RegistrationStatus />} />

      <Route path="/business/:businessId" element={<PublicBusinessWebsite />} />
      <Route path="/book/:businessId" element={<PublicBooking />} />
      <Route path="/book/:businessId/success" element={<PublicBooking successRedirect={true} />} />
      <Route path="/shop/:businessId" element={<PublicShop />} />
      <Route path="/shop/:businessId/product/:productId" element={<ShopProductView />} />
      <Route path="/shop/:businessId/login" element={<ShopLogin />} />
      <Route path="/shop/:businessId/register" element={<ShopRegister />} />
      <Route path="/shop/:businessId/accept-invite" element={<ShopAcceptInvite />} />
      <Route path="/shop/:businessId/track" element={<ShopTrackOrder />} />
      <Route path="/shop/:businessId/forgot-password" element={<ShopForgotPassword />} />
      <Route path="/shop/:businessId/reset-password" element={<ShopResetPassword />} />
      <Route path="/shop/:businessId/dashboard" element={<CustomerDashboard />} />
      <Route path="/shop/:businessId/order-confirmed" element={<OrderConfirmed />} />

      {/* Tenant Portal — separate auth, no sidebar */}
      <Route path="/tenant/login" element={<TenantLogin />} />
      <Route path="/tenant/forgot-password" element={<TenantForgotPassword />} />
      <Route path="/tenant/reset-password" element={<TenantResetPassword />} />
      <Route path="/tenant/accept-invite" element={<TenantAcceptInvite />} />
      <Route path="/tenant/portal" element={<TenantPortal />} />

      {/* Root Redirect */}
      <Route path="/" element={<Navigate to={token ? (getAudience(localStorage.getItem('business_type')) === 'landlord' ? '/landlord/home' : '/dashboard') : '/login'} />} />
      {/* Landlord dashboard alias */}
      <Route path="/landlord/dashboard" element={<Navigate to="/landlord/home" replace />} />

      {/* Dashboard */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}>
          <Dashboard />
        </ProtectedRoute>
      } />

      {/* Customers */}
      <Route path="/customers" element={<Navigate to="/customers/list" />} />
      <Route path="/customers/add" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor','Staff']}>
          <AddCustomer />
        </ProtectedRoute>
      } />
      <Route path="/customers/list" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor']}>
          <CustomerList />
        </ProtectedRoute>
      } />
      <Route path="/customers/edit/:id" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor','Staff']}>
          <EditCustomer />
        </ProtectedRoute>
      } />
      <Route path="/customers/view/:id" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor']}>
          <CustomerView />
        </ProtectedRoute>
      } />
      <Route path="/customers/view/:id/orders" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor']}>
          <CustomerOrders />
        </ProtectedRoute>
      } />

      {/* Products */}
      <Route path="/products" element={<Navigate to="/products/list" />} />
      <Route path="/products/list" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}>
          <ProductList />
        </ProtectedRoute>
      } />
      <Route path="/products/add" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor']}>
          <ProductForm />
        </ProtectedRoute>
      } />
      <Route path="/products/new" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor']}>
          <ProductForm />
        </ProtectedRoute>
      } />
      <Route path="/products/view/:id" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}>
          <ProductView />
        </ProtectedRoute>
      } />
      <Route path="/products/edit/:id" element={
        <ProtectedRoute allowedRoles={['Admin','Manager','Editor']}>
          <ProductEdit />
        </ProtectedRoute>
      } />

      {/* Invoices */}
      <Route path="/invoices" element={<Navigate to="/invoices/list" />} />
      <Route path="/invoices/add" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><InvoiceForm /></ProtectedRoute>} />
      <Route path="/invoices/create" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><InvoiceForm /></ProtectedRoute>} />
      <Route path="/invoices/list" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}><InvoiceList /></ProtectedRoute>} />
      <Route path="/invoices/view/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}><InvoiceView /></ProtectedRoute>} />
      <Route path="/payment/checkout" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><PaymentCheckout /></ProtectedRoute>} />
      <Route path="/invoices/edit/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><InvoiceEdit /></ProtectedRoute>} />

      {/* Credit Notes */}
      <Route path="/credit-notes" element={<ProtectedRoute allowedRoles={['Admin','Editor']}><CreditNotes /></ProtectedRoute>} />

      {/* Orders */}
      <Route path="/orders" element={<Navigate to="/orders/list" />} />
      <Route path="/orders/add" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><OrderForm /></ProtectedRoute>} />
      <Route path="/orders/new" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><OrderForm /></ProtectedRoute>} />
      <Route path="/orders/list" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}><OrderList /></ProtectedRoute>} />
      <Route path="/orders/view/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}><OrderView /></ProtectedRoute>} />
      <Route path="/orders/edit/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><OrderForm /></ProtectedRoute>} />

      {/* Purchase Orders & Stock Movements */}
      <Route path="/purchase-orders" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><PurchaseOrders /></ProtectedRoute>} />
      <Route path="/stock-movements" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}><StockMovements /></ProtectedRoute>} />

      {/* Suppliers */}
      <Route path="/suppliers" element={<Navigate to="/suppliers/list" />} />
      <Route path="/suppliers/list" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><SupplierList /></ProtectedRoute>} />
      <Route path="/suppliers/add" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><SupplierForm /></ProtectedRoute>} />
      <Route path="/suppliers/new" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><SupplierForm /></ProtectedRoute>} />
      <Route path="/suppliers/view/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><SupplierView /></ProtectedRoute>} />
      <Route path="/suppliers/edit/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><SupplierEdit /></ProtectedRoute>} />

      {/* Categories */}
      <Route path="/categories" element={<Navigate to="/categories/list" />} />
      <Route path="/categories/list" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}><CategoryList /></ProtectedRoute>} />
      <Route path="/categories/add" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><CategoryForm /></ProtectedRoute>} />
      <Route path="/categories/view/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer']}><CategoryView /></ProtectedRoute>} />
      <Route path="/categories/edit/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><CategoryEdit /></ProtectedRoute>} />

      {/* Other Modules */}
      <Route path="/accounting" element={<ProtectedRoute allowedRoles={['Admin','Viewer']}><Accounting /></ProtectedRoute>} />
      <Route path="/accounting/aging" element={<Navigate to="/accounting" replace />} />
      <Route path="/shipping" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><Shipping /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><Analytics /></ProtectedRoute>} />
      <Route path="/credit-terms" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><CreditTerms /></ProtectedRoute>} />
      <Route path="/accounts-receivable" element={<Navigate to="/accounting" replace />} />
      <Route path="/booking" element={<ProtectedRoute allowedRoles={['Admin','Manager','Viewer','Staff']}><Booking /></ProtectedRoute>} />
      <Route path="/booking/analytics" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><BookingAnalytics /></ProtectedRoute>} />
      <Route path="/business-settings" element={<ProtectedRoute allowedRoles={['Admin']}><BusinessSettings /></ProtectedRoute>} />
      <Route path="/email-settings" element={<ProtectedRoute allowedRoles={['Admin']}><EmailSettings /></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><StaffManagement /></ProtectedRoute>} />
      <Route path="/admin" element={<SuperAdminRoute><PlatformLayout /></SuperAdminRoute>}>
        <Route index element={<OverviewPage />} />
        <Route path="businesses" element={<BusinessesPage />} />
        <Route path="applications" element={<ApplicationsPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="billing" element={<BillingSupportPage />} />
        <Route path="gdpr" element={<GdprPage />} />
        <Route path="system" element={<SystemPage />} />
      </Route>
      <Route path="/ai-assistant" element={<ProtectedRoute allowedRoles={['Admin','Manager']}><AIAssistant /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><Support /></ProtectedRoute>} />
      <Route path="/send-message" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor']}><SendMessage /></ProtectedRoute>} />
      <Route path="/calendar-settings" element={<ProtectedRoute allowedRoles={['Admin']}><CalendarSettings /></ProtectedRoute>} />
      <Route path="/stripe-connect" element={<ProtectedRoute allowedRoles={['Admin']}><StripeConnectSettings /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute allowedRoles={['Admin']}><AuditLog /></ProtectedRoute>} />

      {/* Landlord */}
      <Route path="/landlord/home" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordDashboard /></ProtectedRoute>} />
      <Route path="/landlord/properties" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordProperties /></ProtectedRoute>} />
      <Route path="/landlord/properties/new" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><PropertyFormPage /></ProtectedRoute>} />
      <Route path="/landlord/properties/:id/edit" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><PropertyFormPage /></ProtectedRoute>} />
      <Route path="/landlord/properties/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><PropertyViewPage /></ProtectedRoute>} />
      <Route path="/landlord/tenants" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordTenants /></ProtectedRoute>} />
      <Route path="/landlord/tenants/new" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><TenantFormPage /></ProtectedRoute>} />
      <Route path="/landlord/tenants/:id/edit" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><TenantFormPage /></ProtectedRoute>} />
      <Route path="/landlord/tenants/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><TenantViewPage /></ProtectedRoute>} />
      <Route path="/landlord/leases" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordLeases /></ProtectedRoute>} />
      <Route path="/landlord/leases/new" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LeaseFormPage /></ProtectedRoute>} />
      <Route path="/landlord/leases/:id/edit" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LeaseFormPage /></ProtectedRoute>} />
      <Route path="/landlord/leases/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LeaseViewPage /></ProtectedRoute>} />
      <Route path="/landlord/pipeline" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordSalesPipeline /></ProtectedRoute>} />
      <Route path="/landlord/pipeline/new" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><PipelineFormPage /></ProtectedRoute>} />
      <Route path="/landlord/pipeline/:id/edit" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><PipelineFormPage /></ProtectedRoute>} />
      <Route path="/landlord/pipeline/:id" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><PipelineViewPage /></ProtectedRoute>} />
      <Route path="/landlord/rent" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordRentTracker /></ProtectedRoute>} />
      <Route path="/landlord/maintenance" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordMaintenance /></ProtectedRoute>} />
      <Route path="/landlord/certificates" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordCertificates /></ProtectedRoute>} />
      <Route path="/landlord/viewings" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordViewings /></ProtectedRoute>} />
      <Route path="/landlord/accounting" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordAccounting /></ProtectedRoute>} />
      <Route path="/landlord/tax-report" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordTaxReport /></ProtectedRoute>} />
      <Route path="/landlord/inventory" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordInventory /></ProtectedRoute>} />
      <Route path="/landlord/audit-log" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><LandlordAuditLog /></ProtectedRoute>} />

      {/* Beauty Suite */}
      <Route path="/beauty/dashboard" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><BeautyDashboard /></ProtectedRoute>} />
      <Route path="/beauty/clients"  element={<Navigate to="/customers" replace />} />
      <Route path="/beauty/bookings" element={<ProtectedRoute allowedRoles={['Admin','Manager','Editor','Viewer','Staff']}><BeautyBookings /></ProtectedRoute>} />
      <Route path="/beauty/services" element={<Navigate to="/products" replace />} />


      {/* Fallback */}
      <Route path="*" element={<Navigate to={token ? (String(role || '').toLowerCase() === 'super_admin' ? '/admin' : (getAudience(localStorage.getItem('business_type')) === 'landlord' ? '/landlord/home' : '/dashboard')) : '/login'} />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <InstallPrompt />
            <Layout />
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

const Layout: React.FC = () => {
  const { token, role } = useAuth();
  const location = useLocation();
  const isPlatform = location.pathname.startsWith('/admin');
  const isSuperAdmin = String(role || '').toLowerCase() === 'super_admin';

  // Super admins use platform console only — never the tenant wholesale/landlord sidebar
  if (token && isSuperAdmin && !isPlatform) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {token && !isPlatform && <Sidebar />}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <main style={{ flex: 1, padding: isPlatform ? 0 : '30px', backgroundColor: isPlatform ? '#0f172a' : '#f4f6f8' }}>
          <AppRoutes />
        </main>
      </div>
    </div>
  );
};

export default App;

